import { Component, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Html } from '@react-three/drei';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import type { PlayerSpot } from './formationProjection';
import { ProceduralFootballer } from './ProceduralFootballer';
import { RiggedFootballer } from './RiggedFootballer';
import { archetypeForRole, useRiggedFootballer } from '../../lib/useRiggedFootballer';

/**
 * Stylized 3D footballer with EA FC-style selection + drag-to-reposition.
 *
 * Visual: ProceduralFootballer (humanoid built from Three.js primitives —
 * no GLB asset, no license review) tinted with the team colour, soft
 * drop shadow on the grass, selection ring + role chip on hover or
 * select. Idle bob lives on the figure itself; outer hover lift 0.12m,
 * drag lift 0.4m provide drag-affordance on top of that.
 *
 * Drag is raycaster-based per spec §16. Pointer events sit on an
 * invisible capsule hitbox around the figure (the procedural meshes
 * are fiddly to hit-test reliably across DPRs and zoom levels). The
 * board disables OrbitControls for the duration via `onDragChange` so
 * the camera doesn't fight the drag.
 */
interface Props {
  spot: PlayerSpot;
  number: number;
  team: 'home' | 'away';
  selected: boolean;
  /** Outer board mode. In 'draw' mode, pointer-down on this token starts
   *  an arrow at the token's position instead of a token-drag. */
  mode: 'move' | 'draw';
  /**
   * When set (during run-path playback), overrides the spot's position so
   * the figure visually traverses its bound curve. Leaves the underlying
   * spot state untouched so players return to base on stop.
   */
  playbackPos?: { x: number; z: number } | null;
  /**
   * Active gait — 'idle' / 'walk' / 'run'. Driven by the parent based on
   * whether this token is currently traversing a bound run-path under
   * auto-play (run) or manual scrub (walk).
   */
  gait?: 'idle' | 'walk' | 'run';
  /**
   * Mirror of `prefers-reduced-motion: reduce`. When true the figure's
   * idle bob, arm sway, and selection-ring pulse are frozen; the rigged
   * footballer's idle/walk/run clips also pause on their first frame.
   * Drag + selection still work fully — only the ambient micromotion
   * stops. Threaded down from the board so we don't re-probe per token.
   */
  reducedMotion?: boolean;
  onSelect: () => void;
  onDragChange: (dragging: boolean) => void;
  onMove: (next: { x: number; z: number }) => void;
  /** Called in 'draw' mode when the coach starts an arrow from this token. */
  onStartArrowFromToken?: (origin: { x: number; z: number }) => void;
}

const HOME_BODY = '#4ade80';
const AWAY_BODY = '#fbbf24';
const HOME_RING = '#22c55e';
const AWAY_RING = '#f59e0b';
// Procedural figure stands ~1.95m unscaled; 1.6× lifts it to ~3.1m world
// units so it reads from the Tactical preset (camera 55m up) without
// dwarfing the field at Broadcast distance.
const FIGURE_SCALE = 1.6;
const HITBOX_RADIUS = 1.1;
const HITBOX_HEIGHT = 4.0;
// Drag plane sits at the figure's foot level so the projected pointer
// matches what the coach intuitively grabs.
const DRAG_PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

/**
 * Tiny error boundary so a broken/missing GLB doesn't take the whole
 * board down. Falls back to the children render-prop on error.
 */
class GltfErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { errored: boolean }
> {
  state = { errored: false };
  static getDerivedStateFromError() { return { errored: true }; }
  componentDidCatch(err: unknown) {
    console.warn('[PlayerToken3D] rigged GLB load failed; using procedural fallback', err);
  }
  render() {
    if (this.state.errored) return this.props.fallback;
    return this.props.children;
  }
}

export function PlayerToken3D({ spot, number, team, selected, mode, playbackPos, gait = 'idle', reducedMotion = false, onSelect, onDragChange, onMove, onStartArrowFromToken }: Props) {
  const teamHex = team === 'home' ? HOME_BODY : AWAY_BODY;
  const ringHex = team === 'home' ? HOME_RING : AWAY_RING;
  const riggedAvailable = useRiggedFootballer();
  const groupRef = useRef<THREE.Group>(null);
  const dragOffset = useRef<THREE.Vector3>(new THREE.Vector3());
  const [isDragging, setIsDragging] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { camera, raycaster, pointer } = useThree();

  // Per-token idle phase so a row of figures doesn't bob in unison.
  // useMemo([number, team]) pins it to the player's slot — a given
  // player's bob is stable across re-renders.
  const idlePhase = useMemo(() => {
    return (number * 1.7 + (team === 'away' ? Math.PI : 0)) % (Math.PI * 2);
  }, [number, team]);

  // Map the formation role (GK / CB / ST / etc.) to a body archetype so
  // the squad reads as four distinct silhouettes — keepers tall, defenders
  // stocky, attackers lean — instead of 22 identical clones.
  const archetype = useMemo(() => archetypeForRole(spot.role), [spot.role]);

  // Project the current pointer (NDC) onto the drag plane and return
  // the world coordinate. Used both on drag-start (to compute the grab
  // offset so the figure doesn't snap to cursor centre) and on each
  // move tick.
  const pointerToWorld = useMemo(() => {
    const target = new THREE.Vector3();
    return (): THREE.Vector3 => {
      raycaster.setFromCamera(pointer, camera);
      raycaster.ray.intersectPlane(DRAG_PLANE, target);
      return target;
    };
  }, [camera, raycaster, pointer]);

  const onPointerDown = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    onSelect();
    if (mode === 'draw') {
      // Draw mode: hand the start position off to the board's arrow
      // layer instead of beginning a token-drag. The arrow grows as
      // the coach continues their drag onto the pitch.
      onStartArrowFromToken?.({ x: spot.x, z: spot.z });
      return;
    }
    setIsDragging(true);
    onDragChange(true);
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const world = pointerToWorld();
    if (groupRef.current) {
      // Compare in world XZ only — the figure has a vertical lift on
      // hover/drag that we don't want to bake into the offset.
      dragOffset.current.set(
        groupRef.current.position.x - world.x,
        0,
        groupRef.current.position.z - world.z,
      );
    }
  };

  const onPointerMove = (e: ThreeEvent<PointerEvent>) => {
    if (!isDragging) return;
    e.stopPropagation();
    const world = pointerToWorld();
    onMove({
      x: world.x + dragOffset.current.x,
      z: world.z + dragOffset.current.z,
    });
  };

  const endDrag = (e: ThreeEvent<PointerEvent>) => {
    if (!isDragging) return;
    e.stopPropagation();
    setIsDragging(false);
    onDragChange(false);
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  };

  // Hovering a token swaps the canvas cursor to a grab/grabbing affordance —
  // matches familiar click-and-drag UIs and tells the coach where the
  // hitbox actually lives. The body cursor is restored when the pointer
  // leaves the token (or when the component unmounts mid-drag).
  useEffect(() => {
    if (!hovered && !isDragging) return;
    const prev = document.body.style.cursor;
    document.body.style.cursor = isDragging ? 'grabbing' : 'grab';
    return () => {
      document.body.style.cursor = prev;
    };
  }, [hovered, isDragging]);

  // Subtle scale pulse on the selection ring — proves to the coach that the
  // figure they tapped really is the active one without screaming for
  // attention. Drives a ref so React state changes don't fire each frame.
  // Under reduced motion we hold the ring at a fixed, slightly-enlarged
  // "selected" scale so it still reads as active without the sine wave.
  const ringRef = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    if (!ringRef.current || !selected) return;
    if (reducedMotion) {
      ringRef.current.scale.set(1, 1, 1);
      return;
    }
    const s = 1 + Math.sin(clock.elapsedTime * 4) * 0.04;
    ringRef.current.scale.set(s, s, 1);
  });

  // Default-facing per team: home looks down -Z (the away goal it attacks),
  // away looks down +Z. Three.js convention is forward = -Z.
  const baseFacing = team === 'away' ? Math.PI : 0;
  const lift = isDragging ? 0.4 : hovered ? 0.12 : 0;

  const renderX = playbackPos ? playbackPos.x : spot.x;
  const renderZ = playbackPos ? playbackPos.z : spot.z;

  // Rotate to face motion direction during run-path playback — without
  // this, the figure's body stays locked to the team base-facing while
  // the legs animate forward, producing a moonwalk effect when the run
  // path isn't aligned with the kicking direction. atan2(dx, -dz) gives
  // the Y rotation that points local -Z (Three.js "forward") along the
  // velocity vector. Smoothed at 18% per frame to avoid jerky snaps on
  // sharp curves.
  const lastPosRef = useRef<{ x: number; z: number } | null>(null);
  const facingRef = useRef(baseFacing);
  useFrame(() => {
    if (!groupRef.current) return;
    if (!playbackPos) {
      // No playback → settle back to the team's resting facing.
      lastPosRef.current = null;
      let delta = baseFacing - facingRef.current;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      facingRef.current += delta * 0.18;
      groupRef.current.rotation.y = facingRef.current;
      return;
    }
    const prev = lastPosRef.current;
    lastPosRef.current = { x: playbackPos.x, z: playbackPos.z };
    if (!prev) return; // first frame of playback — no velocity yet
    const dx = playbackPos.x - prev.x;
    const dz = playbackPos.z - prev.z;
    const speed = Math.hypot(dx, dz);
    if (speed < 1e-4) return; // stationary frame, keep last facing
    const target = Math.atan2(dx, -dz);
    let delta = target - facingRef.current;
    while (delta > Math.PI) delta -= 2 * Math.PI;
    while (delta < -Math.PI) delta += 2 * Math.PI;
    facingRef.current += delta * 0.18;
    groupRef.current.rotation.y = facingRef.current;
  });

  return (
    <group ref={groupRef} position={[renderX, lift, renderZ]} rotation={[0, baseFacing, 0]}>
      {/* Note: per-player drop shadows live in TacticsBoard3D's global
          ContactShadows for soft, realistic falloff. Only the selection
          ring lives here — drawn just above the grass. */}

      {/* Focus ring under the figure when picked or hovered. When
          selected, the ring switches to broadcast yellow (NFL
          telestrator / Sky-MNF "focus this player" idiom) and grows
          a touch thicker so it stands off the team-colour body and
          jersey. Hover keeps the team colour as a subtle affordance.
          useFrame pulses the scale while selected so the active
          token reads at a glance even when stationary. */}
      {(selected || hovered) && (
        <mesh ref={ringRef} position={[0, 0.025 - lift, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry
            args={selected ? [1.35, 1.65, 48] : [1.35, 1.55, 48]}
          />
          <meshBasicMaterial
            color={selected ? 0xfbbf24 : ringHex}
            transparent
            opacity={selected ? 0.95 : 0.55}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}

      {/* The footballer figure itself.
          When /footballer-rigged.glb is present in /public, the
          useRiggedFootballer probe flips to true and we render the
          skinned-mesh path with proper animations. Otherwise (and on
          load failure) the procedural primitives stand in. The rigged
          path is wrapped in a Suspense + ErrorBoundary so a slow / broken
          download never blocks the rest of the board. */}
      <group scale={FIGURE_SCALE}>
        {riggedAvailable ? (
          <GltfErrorBoundary
            fallback={
              <ProceduralFootballer
                teamHex={teamHex}
                selected={selected}
                idlePhase={idlePhase}
                reducedMotion={reducedMotion}
              />
            }
          >
            <Suspense
              fallback={
                <ProceduralFootballer
                  teamHex={teamHex}
                  selected={selected}
                  idlePhase={idlePhase}
                  reducedMotion={reducedMotion}
                />
              }
            >
              <RiggedFootballer
                teamHex={teamHex}
                selected={selected}
                idlePhase={idlePhase}
                gait={gait}
                archetype={archetype}
                reducedMotion={reducedMotion}
              />
            </Suspense>
          </GltfErrorBoundary>
        ) : (
          <ProceduralFootballer
            teamHex={teamHex}
            selected={selected}
            idlePhase={idlePhase}
            reducedMotion={reducedMotion}
          />
        )}
      </group>

      {/* Invisible capsule hitbox — gives drag a generous, predictable
          target regardless of the GLB's mesh layout. */}
      <mesh
        position={[0, HITBOX_HEIGHT / 2, 0]}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <cylinderGeometry args={[HITBOX_RADIUS, HITBOX_RADIUS, HITBOX_HEIGHT, 16]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Jersey number floats just above the figure's head — billboards
          to the camera so it stays legible from any angle. */}
      <Html
        center
        distanceFactor={20}
        position={[0, 5.2, 0]}
        zIndexRange={[10, 0]}
      >
        <span className="t3d-token-number" data-team={team} data-selected={selected || undefined}>
          {number}
        </span>
      </Html>

      {/* Role chip when selected/hovered — keeps resting state clean. */}
      {(selected || hovered) && (
        <Html
          center
          distanceFactor={18}
          position={[0, 6.1, 0]}
          zIndexRange={[20, 0]}
        >
          <span className="t3d-token-role" data-team={team}>{spot.role}</span>
        </Html>
      )}
    </group>
  );
}
